"use client";
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

const DynamicMapContainer = dynamic(() => import('react-leaflet').then(mod => mod.MapContainer), { ssr: false });
const DynamicTileLayer = dynamic(() => import('react-leaflet').then(mod => mod.TileLayer), { ssr: false });

interface RadarPast {
  time: number;
  path: string;
}

interface RadarData {
  radar: {
    past: RadarPast[];
  };
}


const RadarMap = () => {
  const [radarData, setRadarData] = useState<RadarData | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rainviewerUrl, setRainviewerUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [animationSpeed, setAnimationSpeed] = useState(750); // Default to Normal speed (750ms)
  
  // Location state
  const [zipCode, setZipCode] = useState('07960'); // Default zip code
  const [mapCenter, setMapCenter] = useState<[number, number]>([41.019700, -74.376190]); // Default coordinates
  const [townName, setTownName] = useState('Morristown, NJ'); // Default town name
  
  // Image preloading state
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  // Single layer with better timing
  const [currentRadarUrl, setCurrentRadarUrl] = useState('');
  const [nextRadarUrl, setNextRadarUrl] = useState('');
  
  // Control panel state
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 }); // Will be calculated on mount
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasBeenMoved, setHasBeenMoved] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

  // Animation speed options (in milliseconds) - optimized for smoother animation
  const speedOptions = [
    { label: 'Very Slow', value: 1500 },
    { label: 'Slow', value: 1000 },
    { label: 'Normal', value: 750 },
    { label: 'Fast', value: 500 },
    { label: 'Very Fast', value: 300 }
  ];
  

  useEffect(() => {
    // Generate dates including the current day and previous two days
    const generateDates = () => {
      const dates = [];
      for (let i = 0; i <= 1; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        
        // Convert UTC to EST
        const estDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        dates.push(estDate.toISOString().split('T')[0]);
      }
      return dates;
    };
  
    const dates = generateDates();
    setAvailableDates(dates);
    setSelectedDate(dates[0]); // Default to the most recent date
  }, []);

  // Set initial position to bottom right
  useEffect(() => {
    const calculateBottomRightPosition = () => {
      // Use a timeout to ensure the component is mounted and container is available
      setTimeout(() => {
        const mapContainer = document.querySelector('[style*="80vh"]') as HTMLElement;
        if (mapContainer) {
          const mapRect = mapContainer.getBoundingClientRect();
          const panelWidth = 400;
          const panelHeight = 280; // Match the accurate height used in drag handler
          
          const bottomRightPosition = {
            x: mapRect.width - panelWidth - 20,
            y: mapRect.height - panelHeight - 20
          };
          
          setPanelPosition(bottomRightPosition);
          setLastPosition(bottomRightPosition);
        }
      }, 100);
    };

    calculateBottomRightPosition();
  }, []);
  
  

  useEffect(() => {
    if (selectedDate) {
      // Generate timestamps for the selected day, every 10 minutes, including the current time if today is selected
      const generateTimestamps = () => {
        const timestamps = [];
        const now = new Date();
  
        let startOfDay = new Date(selectedDate).setHours(0, 0, 0, 0); // Start at midnight
        let endOfDay = selectedDate === now.toISOString().split('T')[0]
          ? now.getTime() // If the selected date is today, use the current time as the end time
          : new Date(selectedDate).setHours(23, 59, 59, 999); // Otherwise, end at the end of the day
  
        for (let time = startOfDay; time <= endOfDay; time += 10 * 60 * 1000) {
          timestamps.push(Math.floor(time / 1000)); // Store as UNIX timestamp (seconds)
        }
        return timestamps;
      };
  
      const generatedTimestamps = generateTimestamps();
      setTimestamps(generatedTimestamps);
      
      // Initialize the first radar URL
      if (generatedTimestamps.length > 0) {
        const firstUrl = `https://tilecache.rainviewer.com/v2/radar/${generatedTimestamps[0]}/256/{z}/{x}/{y}/4/1_0.png`;
        setCurrentRadarUrl(firstUrl);
        setRainviewerUrl(firstUrl); // Keep for backward compatibility
        
        // Preload images for smoother animation
        preloadRadarImages(generatedTimestamps);
      }
    }
  }, [selectedDate]);
  

  useEffect(() => {
    const fetchRadar = async () => {
      if (!selectedDate) return;

      try {
        // Assume the API can handle fetching radar data for a specific date
        //const response = await fetch(`https://api.rainviewer.com/public/weather-maps.json?date=${selectedDate}`);
        const response = await fetch(`https://api.rainviewer.com/public/weather-maps.json`);
        if (!response.ok) {
          throw new Error('Failed to fetch radar data');
        }
        const data: RadarData = await response.json();

        setRadarData(data);
      } catch (error) {
        console.error('Error fetching radar data:', error);
      }
    };

    fetchRadar();
  }, [selectedDate]);

  useEffect(() => {
    if (timestamps.length > 0 && isPlaying) {
      const intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % timestamps.length;
          return nextIndex;
        });
      }, animationSpeed);

      return () => clearInterval(intervalId);
    }
  }, [timestamps, isPlaying, animationSpeed]);

  useEffect(() => {
    if (timestamps.length > 0) {
      const currentTimestamp = timestamps[currentIndex];
      const newUrl = `https://tilecache.rainviewer.com/v2/radar/${currentTimestamp}/256/{z}/{x}/{y}/4/1_0.png`;
      
      // Only update if the URL has actually changed and image is preloaded
      if (newUrl !== currentRadarUrl) {
        setCurrentRadarUrl(newUrl);
        setRainviewerUrl(newUrl); // Keep for backward compatibility
        
        // Preload next frame for smoother transition
        const nextIndex = (currentIndex + 1) % timestamps.length;
        if (nextIndex !== currentIndex) {
          const nextTimestamp = timestamps[nextIndex];
          const nextUrl = `https://tilecache.rainviewer.com/v2/radar/${nextTimestamp}/256/{z}/{x}/{y}/4/1_0.png`;
          setNextRadarUrl(nextUrl);
          
          // Preload the next image
          const img = new Image();
          img.src = nextUrl.replace('{z}', '9').replace('{x}', '154').replace('{y}', '193'); // Sample tile
        }
      }
    }
  }, [currentIndex, timestamps, currentRadarUrl]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentIndex(parseInt(event.target.value, 10));
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate(event.target.value);
  };

  // Handle minimize/restore with position memory
  const handleMinimizeToggle = () => {
    if (!isMinimized) {
      // Minimizing - hide the panel
      setIsMinimized(true);
    } else {
      // Restoring - show panel at last position if moved, otherwise bottom right
      setIsMinimized(false);
      if (hasBeenMoved) {
        setPanelPosition(lastPosition);
      }
    }
  };

  const handleControlsButtonClick = () => {
    setIsMinimized(false);
    if (hasBeenMoved) {
      setPanelPosition(lastPosition);
    }
  };

  // Handle zip code change and geocoding
  const handleZipCodeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newZipCode = event.target.value;
    setZipCode(newZipCode);
    
    // Only geocode if we have a 5-digit zip code
    if (newZipCode.length === 5 && /^\d{5}$/.test(newZipCode)) {
      try {
        // Using OpenStreetMap Nominatim API for geocoding
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${newZipCode}&countrycodes=us&limit=1`
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const location = data[0];
            const lat = parseFloat(location.lat);
            const lon = parseFloat(location.lon);
            
            setMapCenter([lat, lon]);
            setTownName(location.display_name.split(',').slice(0, 2).join(',') || `${newZipCode}, USA`);
          }
        }
      } catch (error) {
        console.error('Error geocoding zip code:', error);
        setTownName(`${newZipCode}, USA`); // Fallback town name
      }
    }
  };

  // Preload radar images for smoother animation
  const preloadRadarImages = async (timestampArray: number[]) => {
    setIsLoadingImages(true);
    const newPreloadedImages = new Set<string>();
    
    // Preload a few sample tiles from each timestamp to improve caching
    const sampleTiles = [
      { z: 9, x: 154, y: 193 }, // Sample tile coordinates
      { z: 9, x: 155, y: 193 },
      { z: 9, x: 154, y: 194 },
      { z: 9, x: 155, y: 194 }
    ];
    
    const loadPromises = timestampArray.map((timestamp) => {
      return Promise.all(
        sampleTiles.map(tile => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            const url = `https://tilecache.rainviewer.com/v2/radar/${timestamp}/256/${tile.z}/${tile.x}/${tile.y}/4/1_0.png`;
            
            img.onload = () => {
              newPreloadedImages.add(url);
              resolve();
            };
            
            img.onerror = () => {
              // Even if image fails to load, resolve to continue
              resolve();
            };
            
            img.src = url;
          });
        })
      );
    });
    
    await Promise.all(loadPromises);
    setPreloadedImages(newPreloadedImages);
    setIsLoadingImages(false);
  };

  // Panel drag handlers
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    const mapContainer = (event.target as HTMLElement).closest('[style*="80vh"]');
    const mapRect = mapContainer?.getBoundingClientRect();
    
    if (mapRect) {
      setIsDragging(true);
      setDragOffset({
        x: event.clientX - mapRect.left - panelPosition.x,
        y: event.clientY - mapRect.top - panelPosition.y
      });
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging) {
      // Get the map container element
      const mapContainer = document.querySelector('[style*="80vh"]') as HTMLElement;
      
      if (mapContainer) {
        const mapRect = mapContainer.getBoundingClientRect();
        const panelWidth = 400; // Fixed panel width
        // More accurate panel height calculation
        const panelHeight = isMinimized ? 50 : 280; // Account for all content + padding
        
        // Calculate new position relative to map container
        const newX = Math.min(
          Math.max(10, event.clientX - mapRect.left - dragOffset.x),
          mapRect.width - panelWidth - 10
        );
        
        const newY = Math.min(
          Math.max(10, event.clientY - mapRect.top - dragOffset.y),
          mapRect.height - panelHeight - 10
        );
        
        const newPosition = { x: newX, y: newY };
        setPanelPosition(newPosition);
        setLastPosition(newPosition);
        setHasBeenMoved(true);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for mouse events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    return date.toLocaleString('en-US', { timeZone: 'America/New_York' }); // Convert to EST
  };

  if (!rainviewerUrl) {
    return null;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <div style={{ position: 'relative', height: '80vh', width: '80vw', border: '5px solid rgba(128, 128, 128, 0.1)', borderWidth: '2px', borderRadius: '10px', overflow: 'hidden', zIndex: 1 }}>
      <DynamicMapContainer
        center={mapCenter} // Use dynamic center from zip code
        zoom={9}                         // Default zoom level
        style={{ height: '100%', width: '100%' }}
        key={`${mapCenter[0]}-${mapCenter[1]}`} // Force re-render when center changes
      >
        {/* OpenStreetMap Tile Layer */}
        <DynamicTileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* RainViewer Radar Tile Layer - Optimized for smooth transitions */}
        {currentRadarUrl && (
          <DynamicTileLayer
            url={currentRadarUrl}
            attribution='&copy; <a href="https://www.rainviewer.com/">RainViewer</a>'
            opacity={0.6}
            crossOrigin=""
            tileSize={256}
            maxZoom={18}
            updateWhenIdle={false}
            updateWhenZooming={false}
            keepBuffer={2}
            key={currentIndex} // This helps with smoother transitions
          />
        )}
      </DynamicMapContainer>

      {/* Control Panel - only show if not minimized */}
      {!isMinimized && (
        <div style={{ 
          position: 'absolute', 
          top: `${panelPosition.y}px`, 
          left: `${panelPosition.x}px`, 
          zIndex: 1000, 
          background: 'rgba(255, 255, 255, 0.95)', 
          borderRadius: '12px',
          color: '#333',
          minWidth: '400px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          cursor: isDragging ? 'grabbing' : 'default'
        }}>
          {/* Header with drag handle and minimize button */}
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '8px 15px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'grab',
              borderRadius: '12px 12px 0 0',
              background: 'rgba(240, 240, 240, 0.8)'
            }}
            onMouseDown={handleMouseDown}
          >
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#555' }}>
              Radar Controls
            </span>
            <button
              onClick={handleMinimizeToggle}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}
            >
              −
            </button>
          </div>

          {/* Control content */}
          <div style={{ padding: '15px' }}>
            {/* Location Controls */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', gap: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '70px' }}>
                Zip Code:
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={handleZipCodeChange}
                placeholder="Enter zip code"
                maxLength={5}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  width: '100px'
                }}
              />
              <div style={{ 
                fontSize: '13px', 
                color: '#666',
                fontWeight: '500',
                flex: 1
              }}>
                {townName}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <button 
                onClick={handlePlayPause}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)', 
                  color: 'black',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <input
                type="range"
                min="0"
                max={timestamps.length - 1}
                value={currentIndex}
                onChange={handleSliderChange}
                style={{ 
                  marginLeft: '15px', 
                  marginRight: '15px',
                  flex: 1,
                  height: '6px',
                  background: '#e0e0e0',
                  borderRadius: '3px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <select 
                onChange={handleDateChange} 
                value={selectedDate}
                style={{
                  background: 'white',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  fontSize: '14px'
                }}
              >
                {availableDates.map((date, index) => (
                  <option key={index} value={date} style={{ background: 'white', color: '#333' }}>
                    {new Date(date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Speed Control Buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {speedOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => setAnimationSpeed(option.value)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: animationSpeed === option.value ? '#007acc' : '#f5f5f5',
                    color: animationSpeed === option.value ? 'white' : '#333',
                    border: animationSpeed === option.value ? '2px solid #007acc' : '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: animationSpeed === option.value ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            <div style={{ 
              fontSize: '13px', 
              color: '#666',
              textAlign: 'center',
              fontFamily: 'monospace'
            }}>
              {isLoadingImages && (
                <div style={{ marginBottom: '5px', color: '#007acc' }}>
                  Loading radar images...
                </div>
              )}
              {timestamps[currentIndex]
                ? `${formatTimestamp(timestamps[currentIndex])}`
                : 'Loading...'}
            </div>
          </div>
        </div>
      )}

      {/* Controls Button - only show when minimized */}
      {isMinimized && (
        <button
          onClick={handleControlsButtonClick}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#333',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            padding: '10px 15px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          Controls
        </button>
      )}
    </div>
    </div>
  );
};

export default RadarMap;